import React, { useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import SignalCard from "../components/SignalCard";
import { useInfiniteList } from "../lib/useInfiniteList";
import { Loader2, RefreshCw, X } from "lucide-react";

const LAYERS = ["all", "category", "behaviour", "product"];
const GEOS = ["all", "China", "Thailand", "Korea", "Japan", "North America", "India", "Global", "SEA", "Western Europe"];
const SORTS = [
  { value: "newest", label: "Newest first" },
  { value: "velocity", label: "Velocity ↓" },
  { value: "confidence", label: "Confidence ↓" },
  { value: "oldest", label: "Oldest first" },
];

export default function Signals() {
  const [searchParams, setSearchParams] = useSearchParams();
  const citedTitle = searchParams.get("title") || "";
  const [layer, setLayer] = useState("all");
  const [geo, setGeo] = useState("all");
  const [minVel, setMinVel] = useState(0);
  const [sort, setSort] = useState("newest");
  const [refreshTick, setRefreshTick] = useState(0);

  const fetchPage = useCallback((skip, limit) => {
    const params = { skip, limit, min_velocity: minVel, sort };
    if (layer !== "all") params.layer = layer;
    if (geo !== "all") params.geography = geo;
    if (citedTitle) params.title = citedTitle;
    return api.signals(params);
  }, [layer, geo, minVel, sort, refreshTick, citedTitle]);

  const resetKey = `${layer}|${geo}|${minVel}|${sort}|${refreshTick}|${citedTitle}`;
  const { items, total, totalReady, hasMore, loading, sentinelRef } = useInfiniteList(fetchPage, resetKey, 12);

  return (
    <div className="space-y-5">
      <div className="border-b border-white/10 pb-4">
        <div className="label-mono text-bm-amber">SIGNAL EXPLORER</div>
        <h1 className="font-heading text-4xl font-black tracking-tight mt-1" data-testid="page-title">
          Raw consumer telemetry.
        </h1>
        <p className="text-sm text-bm-muted mt-2 max-w-2xl">
          Every signal is an atomic shift detected from commerce, social, or cultural-edge sources. Scroll to load more.
        </p>
      </div>

      {/* Filters */}
      <div className="bm-card p-3 flex flex-wrap items-center gap-4 sticky top-14 z-20 bg-bm-surface/95 backdrop-blur">
        {citedTitle && (
          <button onClick={() => setSearchParams({})} data-testid="cited-title-filter"
            className="flex items-center gap-2 px-2 py-1 text-xs font-mono border border-bm-cyan/60 text-bm-cyan hover:bg-bm-cyan hover:text-black transition-colors max-w-md">
            <span className="truncate">CITED: {citedTitle}</span>
            <X className="w-3 h-3 shrink-0" />
          </button>
        )}
        <div className="flex items-center gap-2">
          <span className="label-mono text-bm-dim">LAYER</span>
          <div className="flex gap-1">
            {LAYERS.map(l => (
              <button key={l} onClick={() => setLayer(l)} data-testid={`filter-layer-${l}`}
                className={`px-2 py-1 text-xs font-mono uppercase border ${layer === l ? "bg-bm-amber text-black border-bm-amber" : "border-white/10 text-bm-muted hover:text-white hover:border-white/30"}`}>
                {l}
              </button>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="label-mono text-bm-dim">GEO</span>
          <select value={geo} onChange={(e) => setGeo(e.target.value)} data-testid="filter-geo"
            className="bg-bm-elevated border border-white/10 text-xs font-mono px-2 py-1 text-white outline-none">
            {GEOS.map(g => <option key={g} value={g}>{g}</option>)}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <span className="label-mono text-bm-dim">SORT</span>
          <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="filter-sort"
            className="bg-bm-elevated border border-white/10 text-xs font-mono px-2 py-1 text-white outline-none">
            {SORTS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </div>
        <div className="flex items-center gap-2 flex-1 min-w-[200px]">
          <span className="label-mono text-bm-dim">MIN VELOCITY</span>
          <input type="range" min={0} max={100} value={minVel} onChange={(e) => setMinVel(+e.target.value)}
            data-testid="filter-velocity" className="flex-1 accent-bm-amber" />
          <span className="font-mono text-xs text-bm-amber w-8">{minVel}</span>
        </div>
        <button onClick={() => setRefreshTick((t) => t + 1)} data-testid="signals-live-btn"
          className="flex items-center gap-2 px-3 py-1.5 text-xs font-mono border border-bm-green/40 text-bm-green hover:bg-bm-green hover:text-black transition-colors">
          <span className="w-1.5 h-1.5 bg-bm-green rounded-full animate-pulse"></span>
          <RefreshCw className="w-3 h-3" />LIVE
        </button>
        <div className="label-mono text-bm-green" data-testid="results-count">{totalReady ? `${items.length} / ${total}` : "…"}</div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="signals-grid">
        {items.map((s, i) => <SignalCard key={s.id} signal={s} idx={i} />)}
      </div>

      {hasMore && (
        <div ref={sentinelRef} className="flex items-center justify-center py-8" data-testid="signals-sentinel">
          <Loader2 className="w-4 h-4 animate-spin text-bm-cyan" />
          <span className="ml-2 font-mono text-xs text-bm-cyan">{loading ? "INGESTING NEXT BATCH..." : "SCROLL FOR MORE"}</span>
        </div>
      )}

      {!hasMore && items.length > 0 && (
        <div className="text-center py-8 font-mono text-xs text-bm-dim" data-testid="signals-end">
          — END OF SIGNAL CORPUS · {total} TRACKED —
        </div>
      )}

      {!loading && items.length === 0 && (
        <div className="text-center py-12 font-mono text-sm text-bm-muted">No signals match these filters.</div>
      )}
    </div>
  );
}
